import { cn } from '@workspace/ui/lib/utils'
import { ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { Prose } from '../../components/prose'
import type { EvalRisk, RiskSeverity } from '../types'

const SEVERITY_STYLES = {
  critical: {
    bg: 'bg-red-500/10',
    text: 'text-red-400',
    border: 'border-red-500/20',
    label: 'Critical',
  },
  high: {
    bg: 'bg-amber-500/10',
    text: 'text-amber-400',
    border: 'border-amber-500/20',
    label: 'High',
  },
  moderate: {
    bg: 'bg-blue-500/10',
    text: 'text-blue-400',
    border: 'border-blue-500/20',
    label: 'Moderate',
  },
} as const

interface RiskGroupProps {
  severity: RiskSeverity
  risks: EvalRisk[]
}

export { RiskGroup as EvaluationRiskGroup }

export function RiskGroup({ severity, risks }: RiskGroupProps) {
  const s = SEVERITY_STYLES[severity]
  return (
    <div className={cn('rounded-xl border', s.border, 'bg-card/20')}>
      <div className="flex items-center gap-2 px-5 py-2.5">
        <span className={cn('size-2 rounded-full', s.bg.replace('/10', ''))} />
        <span className={cn('text-[10px] font-semibold uppercase tracking-wider', s.text)}>
          {s.label}
        </span>
        <span className="text-[10px] text-muted-foreground/80">
          {risks.length} issue{risks.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="border-t border-border/50">
        {risks.map((risk, i) => (
          <RiskItem key={risk.label} risk={risk} isLast={i === risks.length - 1} />
        ))}
      </div>
    </div>
  )
}

function RiskItem({ risk, isLast }: { risk: EvalRisk; isLast: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <div className={cn(!isLast && 'border-b border-border/30')}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-white/[0.02]"
      >
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground/90">{risk.label}</p>
        </div>
        <ChevronDown
          className={cn(
            'size-3.5 shrink-0 text-muted-foreground/80 transition-transform',
            open && 'rotate-180'
          )}
        />
      </button>
      {open && (
        <div className="px-5 pb-4">
          <Prose className="text-xs leading-relaxed text-muted-foreground">{risk.detail}</Prose>
        </div>
      )}
    </div>
  )
}
